import React from "react";
import { View, Text, Image, StyleSheet } from "react-native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { Reveal, LiveDot, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

const FEATURES = [
  { num: "01", title: "Multi-extension cabins",     desc: "Two extensions per cabin (one for guests, one for crew) without a second device. The dual-prefix scheme is built into the platform." },
  { num: "02", title: "Calling cards & rate plans", desc: "Issue crew calling cards with per-route rates and balance limits. Recharge from port, deplete at sea, never overspend." },
  { num: "03", title: "Satellite-tolerant routing", desc: "Dialplans degrade gracefully when satellite drops. Local calls keep flowing, outbound queues, and the bridge stays reachable." },
];

export default function NtVoxcraftMaritimeDeepDive() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  return (
    <View nativeID="maritime" style={[styles.section, { backgroundColor: theme.bg2, paddingVertical: isMobile ? 64 : 110, borderColor: theme.borderSoft }]}>
      <View style={styles.container}>
        <Reveal>
          <Text style={[styles.eyebrow, { color: theme.green }]}>⚓ AT SEA</Text>
          <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>When the PBX leaves shore.</Text>
          <Text style={[styles.lede, { color: theme.textDim }]}>
            On cruise ships and naval vessels, the same platform delivers offship calling, crew dialing,
            passenger services, and satellite-tolerant routing, running on hardware the size of a pizza box,
            deployable from a port call.
          </Text>
        </Reveal>

        <View style={[styles.photosRow, isMobile && styles.photosRowMobile]}>
          <Reveal style={[styles.photoBigOuter, isMobile && styles.photoBigOuterMobile]}>
            <WebClass className="voxcraft-mar-photo" style={[styles.photoInner, { borderColor: theme.borderSoft }]}>
              <Image style={styles.photo} source={require("../../../assets/images/marketing/voxcraft-onboard-deployment.jpg")} />
              <PhotoTag label="Onboard deployments" theme={theme} />
            </WebClass>
          </Reveal>

          <View style={[styles.photosCol, isMobile && styles.photosColMobile]}>
            <Reveal delay={1} style={[styles.photoSmOuter, styles.photoSmOuterFirst, isMobile && styles.photoSmOuterMobile]}>
              <WebClass className="voxcraft-mar-photo" style={[styles.photoInner, { borderColor: theme.borderSoft }]}>
                <Image style={styles.photo} source={require("../../../assets/images/marketing/voxcraft-offship-calling.jpg")} />
                <PhotoTag label="Offship calling" theme={theme} />
              </WebClass>
            </Reveal>
            <Reveal delay={2} style={[styles.photoSmOuter, isMobile && styles.photoSmOuterMobile]}>
              <WebClass className="voxcraft-mar-photo" style={[styles.photoInner, { borderColor: theme.borderSoft }]}>
                <Image style={styles.photo} source={require("../../../assets/images/marketing/voxcraft-crew-comms.jpg")} />
                <PhotoTag label="Crew comms & calling cards" theme={theme} />
              </WebClass>
            </Reveal>
          </View>
        </View>

        <View style={[styles.featRow, isMobile && styles.featRowMobile]}>
          {FEATURES.map((f, i) => (
            <Reveal
              key={i}
              delay={(i % 3) + 1}
              style={[
                styles.featOuter,
                isMobile && styles.featOuterMobile,
                !isMobile && i === FEATURES.length - 1 && { marginRight: 0 },
              ]}
            >
              <WebClass className="voxcraft-mar-feat" style={[styles.featCard, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                <Text style={[styles.featNum, { color: theme.green }]}>{f.num}</Text>
                <Text style={[styles.featTitle, { color: theme.text }]}>{f.title}</Text>
                <Text style={[styles.featDesc, { color: theme.textDim }]}>{f.desc}</Text>
              </WebClass>
            </Reveal>
          ))}
        </View>
      </View>
    </View>
  );
}

function PhotoTag({ label, theme }) {
  return (
    <View style={[styles.tag, { backgroundColor: "rgba(4,5,6,0.85)", borderColor: theme.borderSoft }]}>
      <LiveDot style={{ marginRight: 7 }} />
      <Text style={styles.tagText}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28, borderTopWidth: 1, borderBottomWidth: 1 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27, maxWidth: 720, marginBottom: 56 },

  photosRow: { flexDirection: "row", marginBottom: 56, minHeight: 480 },
  photosRowMobile: { flexDirection: "column", minHeight: 0 },
  // outer carries flex sizing; inner carries visual + transform so :hover scales.
  photoBigOuter: { flex: 1.4, marginRight: 16, minHeight: 480 },
  photoSmOuter:  { flex: 1, minHeight: 220 },
  photoSmOuterFirst: { marginBottom: 16 },
  photoInner: { flex: 1, borderRadius: 16, borderWidth: 1, overflow: "hidden", position: "relative" },
  photosCol: { flex: 1 },
  // On mobile the row stacks into a column. The flex shorthand sets flexBasis:0,
  // which collapses these to ~0 height in an indefinite-height column (and a bare
  // `flex: undefined` doesn't reliably cancel it). Reset the flex props explicitly
  // and give each photo a real height so they don't collapse under the next row.
  photoBigOuterMobile: { flexGrow: 0, flexShrink: 0, flexBasis: "auto", minHeight: 0, height: 260, marginRight: 0 },
  photosColMobile: { flexGrow: 0, flexShrink: 0, flexBasis: "auto", marginTop: 16 },
  photoSmOuterMobile: { flexGrow: 0, flexShrink: 0, flexBasis: "auto", minHeight: 0, height: 200 },
  photo: { width: "100%", height: "100%", position: "absolute", resizeMode: "cover" },
  tag: {
    position: "absolute", bottom: 16, left: 16,
    flexDirection: "row", alignItems: "center",
    paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 999, borderWidth: 1,
  },
  tagText: { color: "#fff", fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 0.6 },

  featRow: { flexDirection: "row" },
  featRowMobile: { flexDirection: "column" },
  featOuter: { flex: 1, marginRight: 16 },
  featOuterMobile: { width: "100%", flex: undefined, marginRight: 0, marginBottom: 12 },
  featCard: { flex: 1, padding: 22, borderRadius: 12, borderWidth: 1 },
  featNum: { fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: "800", letterSpacing: -1, marginBottom: 10 },
  featTitle: { fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: "700", marginBottom: 8 },
  featDesc: { fontSize: 13.5, lineHeight: 20 },
});
